// The scope an issue screen (the board or the table) renders. A single
// workflow groups by stage and enables stage drag/reorder + kanban
// subscriptions; a team or member spans many workflows, so those features are
// off and the board groups by workflow instead. Both screens take an
// IssueScope prop and branch on `scopeWorkflowId(scope) !== undefined` — the
// one switch for "stage features available". Pure (no React) so it can be
// reused anywhere and unit-tested.

export type IssueScope =
  | { kind: "workflow"; workflowId: string }
  | { kind: "team"; teamId: string }
  | { kind: "member"; memberId: string };

/** The mandatory predicate that scopes every issue query for this screen. */
export function scopeWhere(scope: IssueScope): Record<string, unknown> {
  switch (scope.kind) {
    case "workflow":
      return { workflowId: { eq: scope.workflowId } };
    case "team":
      return { assigneeTeamIds: { hasSome: [scope.teamId] } };
    case "member":
      return { assignees: { entityId: { hasSome: [scope.memberId] } } };
  }
}

/**
 * The workflow id when the scope is a single workflow, else undefined. Its
 * presence gates everything workflow-specific: stage grouping/drag,
 * kanban subscriptions, the stage-co-fetching list query, and bulk change-stage.
 */
export function scopeWorkflowId(scope: IssueScope): string | undefined {
  return scope.kind === "workflow" ? scope.workflowId : undefined;
}

/** Default board grouping: stage for a single workflow, workflow across many. */
export function defaultBoardGrouping(scope: IssueScope): "stage" | "workflow" {
  return scope.kind === "workflow" ? "stage" : "workflow";
}

/** Stable string identity for React keys that must reset per scope. */
export function scopeKey(scope: IssueScope): string {
  switch (scope.kind) {
    case "workflow":
      return `workflow:${scope.workflowId}`;
    case "team":
      return `team:${scope.teamId}`;
    case "member":
      return `member:${scope.memberId}`;
  }
}

/** The bare entity id the scope targets, regardless of kind. */
export function scopeEntityId(scope: IssueScope): string {
  switch (scope.kind) {
    case "workflow":
      return scope.workflowId;
    case "team":
      return scope.teamId;
    case "member":
      return scope.memberId;
  }
}
